import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Bell, AlertTriangle, ClipboardList, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRACKER_STATUS = { open: "Open", in_progress: "In Progress", deployed: "Deployed", rejected: "Rejected", postponed: "Postponed" };
const itDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

const RENDERERS = {
  red_alert: (n) => ({ icon: AlertTriangle, color: "text-red-500", text: <><b>{n.client_name}</b> è in fascia rossa</>, sub: `Score ${n.score}/100` }),
  tracker_status: (n) => ({ icon: ClipboardList, color: "text-blue-600", text: <><b>{n.item_title}</b>: status {TRACKER_STATUS[n.from_status] || n.from_status} → <b>{TRACKER_STATUS[n.to_status] || n.to_status}</b></>, sub: `da ${n.actor_name}` }),
  tracker_deploy_date: (n) => ({ icon: CalendarClock, color: "text-emerald-600", text: <><b>{n.item_title}</b>: deploy {n.previous_date ? "spostato" : "previsto"} al <b>{itDate(n.deployment_estimated)}</b></>, sub: `da ${n.actor_name}` }),
};

const NotificationItem = ({ n, onClick }) => {
  const r = (RENDERERS[n.type] || (() => ({ icon: Bell, color: "text-muted-foreground", text: n.title || "Notifica", sub: "" })))(n);
  const Icon = r.icon;
  return (
    <button onClick={onClick} className={`w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-muted/50 transition-colors flex gap-2 ${n.read ? "" : "bg-blue-50/60"}`} data-testid={`notification-${n.notification_id}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${r.color}`} />
      <div className="min-w-0">
        <p className="text-sm">{r.text}</p>
        <p className="text-xs text-muted-foreground">{r.sub}{r.sub ? " · " : ""}{new Date(n.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</p>
      </div>
    </button>
  );
};

export const NotificationsBell = ({ onOpen }) => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/notifications`)).data); } catch { /* silent poll */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) await axios.post(`${API}/notifications/${n.notification_id}/read`).catch(() => {});
    setOpen(false);
    load();
    onOpen(n);
  };
  const readAll = async () => { await axios.post(`${API}/notifications/read-all`).catch(() => {}); load(); };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-md border border-input flex items-center justify-center hover:bg-muted transition-colors" data-testid="notifications-bell">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-count">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Notifiche</span>
          {data.unread > 0 && <button className="text-xs text-primary hover:underline" onClick={readAll} data-testid="notifications-read-all">Segna tutte come lette</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.notifications.length === 0
            ? <p className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="notifications-empty">Nessuna notifica</p>
            : data.notifications.map(n => <NotificationItem key={n.notification_id} n={n} onClick={() => openItem(n)} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
};
